export interface VastEnvironmentSignals {
  isCtv: boolean;
  contentUrl?: string;
  appId?: string;
  appName?: string;
  sessionId?: string;
  deviceId?: string;
  deviceIdType?: string;
  limitAdTracking?: boolean;
  deviceTypeHint?: number;
}

const AIRY_ANDROID_APP_ID = "com.freeairytv.android";
const AIRY_WEBOS_APP_ID    = "com.freeairytv.webos";
const AIRY_TIZEN_APP_ID    = "com.freeairytv.samsung";
const AIRY_ROKU_APP_ID     = "com.freeairytv.roku";
const AIRY_APP_NAME = "AiryTV Movies & TV";
const AIRY_DEFAULT_CONTENT_URL = "https://live.airy.tv";
const CTV_SESSION_STORAGE_KEY = "stormcloud.ctv.sid";
export const PLAYER_ID_STORAGE_KEY = "stormcloud.player.id";

const LG_TEST_APP_ID      = "com.hulu.plus";
const LG_TEST_APP_NAME    = "Hulu";
const LG_TEST_CONTENT_URL = "https://www.hulu.com/live-tv";

let _lgTestDeviceId: string | undefined;
function getLgTestDeviceId(): string {
  if (!_lgTestDeviceId) _lgTestDeviceId = createUuid();
  return _lgTestDeviceId;
}

function isLgWebOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Web0S|webOS|LG Browser|LGSTB|LGE/i.test(navigator.userAgent);
}

function getDefaultAppId(): string {
  if (typeof navigator === "undefined") return AIRY_ANDROID_APP_ID;
  const ua = navigator.userAgent;
  if (/Web0S|webOS|LG Browser|LGSTB|LGE/i.test(ua)) return AIRY_WEBOS_APP_ID;
  if (/Tizen|Samsung/i.test(ua))                       return AIRY_TIZEN_APP_ID;
  if (/Roku/i.test(ua))                                return AIRY_ROKU_APP_ID;
  return AIRY_ANDROID_APP_ID;
}

const DEVICE_ID_STORAGE_KEYS = [
  "rdid",
  "ifa",
  "advertisingId",
  "advertising_id",
  "deviceAdvertisingId",
  "aaid",
  "adid",
  "rida",
  "tifa",
  "lgudid",
];

const ANDROID_ID_TYPES = new Set(["aaid", "adid"]);

export function resolveVastEnvironmentSignals(isCtv: boolean): VastEnvironmentSignals {
  const bridgeSignals  = readNativeBridgeSignals();
  const platformDevice = readPlatformNativeDeviceId();
  const lg = isCtv && isLgWebOS();

  const deviceId =
    bridgeSignals.deviceId  ||
    readStoredDeviceId()    ||
    platformDevice.deviceId ||
    (lg && platformDevice.limitAdTracking !== true ? getLgTestDeviceId() : undefined);
  const deviceIdType =
    bridgeSignals.deviceIdType  ||
    readStoredString("deviceIdType") ||
    readStoredString("idtype") ||
    platformDevice.deviceIdType ||
    inferDeviceIdType() ||
    (lg ? "lgudid" : undefined);
  const platformLat = bridgeSignals.deviceId
    ? bridgeSignals.limitAdTracking
    : platformDevice.limitAdTracking;
  const limitAdTracking =
    deviceId != null
      ? platformLat ?? readStoredLimitAdTracking() ?? false
      : undefined;

  if (isCtv) {
    return {
      isCtv: true,
      contentUrl:
        bridgeSignals.contentUrl ||
        readStoredString("contentUrl") ||
        (lg ? LG_TEST_CONTENT_URL : AIRY_DEFAULT_CONTENT_URL),
      appId:
        bridgeSignals.appId ||
        readStoredString("appId") ||
        readStoredString("msid") ||
        (lg ? LG_TEST_APP_ID : getDefaultAppId()),
      appName:
        bridgeSignals.appName ||
        readStoredString("appName") ||
        readStoredString("an") ||
        (lg ? LG_TEST_APP_NAME : AIRY_APP_NAME),
      sessionId: getOrCreateCtvSessionId(),
      ...(deviceId ? { deviceId } : {}),
      ...(deviceIdType ? { deviceIdType } : {}),
      ...(limitAdTracking != null ? { limitAdTracking } : {}),
      deviceTypeHint: 5,
    };
  }

  return {
    isCtv: false,
    ...(deviceId ? { deviceId } : {}),
    ...(deviceIdType ? { deviceIdType } : {}),
    ...(limitAdTracking != null ? { limitAdTracking } : {}),
  };
}

export function inferDeviceIdType(): string | undefined {
  if (typeof navigator === "undefined") {
    return undefined;
  }

  const ua = navigator.userAgent;
  if (/Roku/i.test(ua)) return "rida";
  if (/Tizen|Samsung/i.test(ua)) return "tifa";
  if (/AppleTV|Apple TV/i.test(ua)) return "tvOS";
  if (/AFT|Fire TV|Amazon/i.test(ua)) return "afai";
  if (/Web0S|webOS|LG Browser|LGSTB|LGE/i.test(ua)) return "lgudid";
  if (/Android|Google TV/i.test(ua)) return "aaid";
  if (/iPhone|iPad|iPod/i.test(ua)) return "idfa";
  return undefined;
}

export function isEquivalentDeviceIdType(
  tagType: string | undefined,
  runtimeType: string | undefined
): boolean {
  if (!tagType || !runtimeType) return false;
  const tag = tagType.toLowerCase();
  const runtime = runtimeType.toLowerCase();
  if (tag === runtime) return true;
  return ANDROID_ID_TYPES.has(tag) && ANDROID_ID_TYPES.has(runtime);
}

export function resolveNativeDeviceId(): string | undefined {
  const bridgeSignals = readNativeBridgeSignals();
  const platformDevice = readPlatformNativeDeviceId();
  const deviceId =
    bridgeSignals.deviceId ||
    readStoredDeviceId() ||
    platformDevice.deviceId;

  if (!deviceId || isMacroPlaceholder(deviceId) || isZeroedAdId(deviceId)) {
    return undefined;
  }

  return deviceId;
}

export function getOrCreateStoredUuid(storageKey: string): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const existing = readPersistentId(storageKey);
  if (existing) {
    writePersistentId(storageKey, existing);
    return existing;
  }

  const id = createUuid();
  writePersistentId(storageKey, id);
  return id;
}

function getOrCreateCtvSessionId(): string {
  const existing = readPersistentId(CTV_SESSION_STORAGE_KEY);
  if (existing) {
    writePersistentId(CTV_SESSION_STORAGE_KEY, existing);
    return existing;
  }

  const sessionId = createUuid();
  writePersistentId(CTV_SESSION_STORAGE_KEY, sessionId);
  return sessionId;
}

function readPersistentId(storageKey: string): string | undefined {
  return readStoredString(storageKey) || readCookie(storageKey);
}

function writePersistentId(storageKey: string, value: string): void {
  try {
    window.localStorage?.setItem(storageKey, value);
  } catch {}
  writeCookie(storageKey, value);
}

const PERSISTENT_ID_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 5; // ~5 years

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  try {
    const prefix = encodeURIComponent(name) + "=";
    const parts = document.cookie ? document.cookie.split(";") : [];
    for (const part of parts) {
      const entry = part.trim();
      if (entry.startsWith(prefix)) {
        return decodeURIComponent(entry.slice(prefix.length)) || undefined;
      }
    }
  } catch {}

  return undefined;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === "undefined") {
    return;
  }

  try {
    document.cookie =
      encodeURIComponent(name) +
      "=" +
      encodeURIComponent(value) +
      ";path=/;max-age=" +
      PERSISTENT_ID_COOKIE_MAX_AGE +
      ";SameSite=Lax";
  } catch {}
}

function createUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    let seed = Date.now() ^ Math.floor(performanceNow() * 1e3);
    for (let i = 0; i < 16; i++) {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      bytes[i] = (seed ^ Math.floor(Math.random() * 256)) & 0xff;
    }
  }

  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  let hex = "";
  for (let i = 0; i < 16; i++) {
    hex += (bytes[i] ?? 0).toString(16).padStart(2, "0");
  }

  return (
    hex.slice(0, 8) +
    "-" +
    hex.slice(8, 12) +
    "-" +
    hex.slice(12, 16) +
    "-" +
    hex.slice(16, 20) +
    "-" +
    hex.slice(20, 32)
  );
}

function performanceNow(): number {
  try {
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
      return performance.now();
    }
  } catch {}
  return 0;
}

function readPlatformNativeDeviceId(): {
  deviceId?: string;
  deviceIdType?: string;
  limitAdTracking?: boolean;
} {
  if (typeof window === "undefined") return {};

  try {
    const adinfo = (window as any).webapis?.adinfo;
    if (adinfo) {
      const tifa: unknown =
        typeof adinfo.getTIFA === "function" ? adinfo.getTIFA() : adinfo.tifa;
      if (typeof tifa === "string" && tifa && !isMacroPlaceholder(tifa)) {
        const lat: unknown =
          typeof adinfo.isLATEnabled === "function"
            ? adinfo.isLATEnabled()
            : undefined;
        return {
          deviceId: tifa,
          deviceIdType: "tifa",
          ...(typeof lat === "boolean" ? { limitAdTracking: lat } : {}),
        };
      }
    }
  } catch {}

  try {
    const webOSDev = (window as any).webOSDev;
    if (webOSDev) {
      const lgudid: unknown =
        typeof webOSDev.LGUDID === "function"
          ? webOSDev.LGUDID()
          : webOSDev.lgudid ?? webOSDev.LGUDID;
      if (typeof lgudid === "string" && lgudid) {
        if (isZeroedAdId(lgudid)) {
          return { deviceIdType: "lgudid", limitAdTracking: true };
        }
        if (!isMacroPlaceholder(lgudid)) {
          return { deviceId: lgudid, deviceIdType: "lgudid", limitAdTracking: false };
        }
      }
    }
  } catch {}

  try {
    const roku = (window as any).Roku;
    if (roku) {
      const rida: unknown =
        typeof roku.getPublisherUniqueId === "function"
          ? roku.getPublisherUniqueId()
          : roku.rida;
      if (typeof rida === "string" && rida && !isMacroPlaceholder(rida)) {
        return { deviceId: rida, deviceIdType: "rida", limitAdTracking: false };
      }
    }
  } catch {}

  return {};
}

const ZEROED_AD_ID_PATTERNS: ReadonlySet<string> = new Set([
  "38400000-8cf0-11bd-b23e-10b96e40000d",
  "00000000-0000-0000-0000-000000000000",
]);

function isMacroPlaceholder(value: string): boolean {
  const t = value.trim();
  return !t ||
    /^(\[[^\]]*\]|\{[^}]*\}|%%[^%]*%%)$/.test(t) ||
    /^(unknown|null|undefined|none|n\/a|\$\{[^}]+\})$/i.test(t) ||
    ZEROED_AD_ID_PATTERNS.has(t.toLowerCase());
}

function isZeroedAdId(value: string): boolean {
  return ZEROED_AD_ID_PATTERNS.has(value.trim().toLowerCase());
}

function readNativeBridgeSignals(): {
  contentUrl?: string;
  appId?: string;
  appName?: string;
  deviceId?: string;
  deviceIdType?: string;
  limitAdTracking?: boolean;
} {
  if (typeof window === "undefined") {
    return {};
  }

  const candidates = [
    (window as any).__STORMCLOUD_CTV_AD_INFO__,
    (window as any).__STORMCLOUD_CTV__,
    (window as any).stormcloudCtv,
    (window as any).AiryTV,
    (window as any).Android,
    (window as any).webOS,
    (window as any).tizen,
    (window as any).amazon,
  ].filter(Boolean);

  for (const source of candidates) {
    const contentUrl =
      readBridgeValue(source, ["contentUrl", "url", "videoUrl", "canonicalUrl"]) ||
      undefined;
    const appId =
      readBridgeValue(source, ["appId", "msid", "applicationId", "packageName"]) ||
      undefined;
    const appName =
      readBridgeValue(source, ["appName", "an", "applicationName"]) || undefined;
    const deviceId =
      readBridgeValue(source, [
        "rdid",
        "ifa",
        "advertisingId",
        "adId",
        "deviceId",
        "lgudid",
        "LGUDID",
        "tifa",
        "rida",
        "afai",
        "aaid",
      ]) || undefined;

    if (!contentUrl && !deviceId && !appId && !appName) continue;

    const deviceIdType =
      readBridgeValue(source, [
        "idtype",
        "deviceIdType",
        "advertisingIdType",
        "idType",
      ]) || inferDeviceIdType();
    const limitAdTracking = readBridgeBoolean(source, [
      "is_lat",
      "limitAdTracking",
      "limitedAdTracking",
      "lat",
    ]);

    return {
      ...(contentUrl ? { contentUrl } : {}),
      ...(appId ? { appId } : {}),
      ...(appName ? { appName } : {}),
      ...(deviceId ? { deviceId } : {}),
      ...(deviceId && deviceIdType ? { deviceIdType } : {}),
      ...(deviceId && limitAdTracking != null ? { limitAdTracking } : {}),
    };
  }

  return {};
}

function readBridgeValue(source: any, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === "string" && value) {
      return value;
    }
    if (typeof value === "function") {
      try {
        const result = value.call(source);
        if (typeof result === "string" && result) {
          return result;
        }
      } catch {}
    }
  }
  return undefined;
}

function readBridgeBoolean(source: any, keys: string[]): boolean | undefined {
  for (const key of keys) {
    const value = source?.[key];
    const normalized = normalizeBoolean(value);
    if (normalized != null) {
      return normalized;
    }
    if (typeof value === "function") {
      try {
        const result = normalizeBoolean(value.call(source));
        if (result != null) {
          return result;
        }
      } catch {}
    }
  }
  return undefined;
}

function readStoredDeviceId(): string | undefined {
  for (const key of DEVICE_ID_STORAGE_KEYS) {
    const value = readStoredString(key);
    if (value) {
      return value;
    }
  }
  return undefined;
}

function readStoredString(key: string): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    const value = window.localStorage?.getItem(key);
    return value || undefined;
  } catch {
    return undefined;
  }
}

function readStoredLimitAdTracking(): boolean | undefined {
  return normalizeBoolean(
    readStoredString("limitAdTracking") ||
      readStoredString("limitedAdTracking") ||
      readStoredString("is_lat")
  );
}

function normalizeBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1;
  }
  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    if (normalized === "1" || normalized === "true" || normalized === "yes") {
      return true;
    }
    if (normalized === "0" || normalized === "false" || normalized === "no") {
      return false;
    }
  }
  return undefined;
}
